import React from "react";
import styled from "styled-components";
import { capitalize, head, range } from "lodash";
import { ChevronDown } from "@sonder/ui/source/icons";
import { FooterSection } from "@sonder/ui/source/styled/components/DateTimePicker";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { colors } from "@sonder/ui/source/styled/theme";
import { TaskRecurrenceSetting, TaskSubjectType } from "core/queries/GetTask";
import { DaysOfWeek, RecurrenceIntervalUnit } from "typings/globalGQLTypes";

// Types
type MutableRecurrenceFields = "intervalUnit" | "intervalAmt" | "daysOfWeek";
export type PartialRecurrenceSetting = Pick<
  TaskRecurrenceSetting,
  MutableRecurrenceFields
>;
interface DropdownOption<Value> {
  value: Value;
  label: string;
}
type DayOfWeekOption = DropdownOption<DaysOfWeek | null>;
type IntervalUnitOption = DropdownOption<RecurrenceIntervalUnit>;

// Fragments
const StyledDropdown = styled(Dropdown)`
  margin-bottom: 1rem;
  margin-right: 0.714rem;
`;

const StyledDropdownButton = styled(Button)`
  border-radius: 0;
  border: none;
  justify-content: left;
  min-height: 2.3rem;
  min-width: 3.5rem;
`;

const StyledDropdownContent = styled(DropdownContent)`
  flex-direction: column;
  max-height: 20rem;
  overflow-y: auto;
`;

const StyledLabel = styled.div`
  color: ${colors.gray6};
  font-size: 0.857rem;
  font-weight: bold;
  margin-bottom: 0.714rem;
`;

// Constants
export const dayOfWeekOptions: DayOfWeekOption[] = [
  { value: null, label: "Completion Day" },
  { value: DaysOfWeek.sunday, label: "Sunday" },
  { value: DaysOfWeek.monday, label: "Monday" },
  { value: DaysOfWeek.tuesday, label: "Tuesday" },
  { value: DaysOfWeek.wednesday, label: "Wednesday" },
  { value: DaysOfWeek.thursday, label: "Thursday" },
  { value: DaysOfWeek.friday, label: "Friday" },
  { value: DaysOfWeek.saturday, label: "Saturday" },
];

export const intervalUnitOptions: IntervalUnitOption[] = [
  { value: RecurrenceIntervalUnit.day, label: "Day" },
  { value: RecurrenceIntervalUnit.week, label: "Week" },
  { value: RecurrenceIntervalUnit.month, label: "Month" },
];

export const validRecurrenceSubjects = [
  TaskSubjectType.Building,
  TaskSubjectType.City,
  TaskSubjectType.CleaningRequest,
  TaskSubjectType.Space,
  TaskSubjectType.Unit,
];

interface TaskRecurrenceProps {
  recurrence: PartialRecurrenceSetting | null;
  setRecurrence(nextRecurrence: PartialRecurrenceSetting | null): void;
  subjectType: TaskSubjectType;
}
export const TaskRecurrence = ({
  recurrence,
  setRecurrence,
  subjectType,
}: TaskRecurrenceProps) => {
  // Only show recurrence settings for building, city, and unit tasks
  if (!validRecurrenceSubjects.includes(subjectType)) return null;

  // No recurrence settings yet, show "Add repeat" button
  if (!recurrence)
    return (
      <FooterSection>
        <Link
          onClick={() =>
            requestAnimationFrame(() =>
              setRecurrence({
                intervalAmt: 1,
                intervalUnit: RecurrenceIntervalUnit.week,
                daysOfWeek: [],
              }),
            )
          }
        >
          Add Repeat
        </Link>
      </FooterSection>
    );

  const onDayOfWeekClick = (daysOfWeek: DaysOfWeek[]) => {
    setRecurrence({ ...recurrence, daysOfWeek });
  };

  const onIntervalAmountClick = (intervalAmt: number) => {
    setRecurrence({ ...recurrence, intervalAmt });
  };

  const onIntervalUnitClick = (intervalUnit: RecurrenceIntervalUnit) => {
    const isWeekdayRecurring = intervalUnit === RecurrenceIntervalUnit.week;
    setRecurrence({
      ...recurrence,
      intervalUnit,
      daysOfWeek: isWeekdayRecurring ? recurrence.daysOfWeek : [],
    });
  };

  const { intervalAmt, intervalUnit, daysOfWeek } = recurrence;
  const dayOfWeek = head(daysOfWeek);
  return (
    <FooterSection>
      <StyledLabel>Repeats Every:</StyledLabel>
      <InputGroup>
        <StyledDropdown
          trigger={
            <Button iconRight>
              {intervalAmt}
              <ChevronDown />
            </Button>
          }
        >
          {({ isOpen, closePortal }) =>
            isOpen && (
              <StyledDropdownContent>
                {range(1, 31).map(value => (
                  <StyledDropdownButton
                    key={value}
                    onClick={() => {
                      requestAnimationFrame(() => {
                        closePortal();
                        onIntervalAmountClick(value);
                      });
                    }}
                  >
                    {value}
                  </StyledDropdownButton>
                ))}
              </StyledDropdownContent>
            )
          }
        </StyledDropdown>
        <StyledDropdown
          trigger={
            <Button iconRight>
              {capitalize(intervalUnit)}
              <ChevronDown />
            </Button>
          }
        >
          {({ isOpen, closePortal }) =>
            isOpen && (
              <StyledDropdownContent>
                {intervalUnitOptions.map(option => (
                  <StyledDropdownButton
                    key={option.label}
                    onClick={() => {
                      requestAnimationFrame(() => {
                        closePortal();
                        onIntervalUnitClick(option.value);
                      });
                    }}
                  >
                    {option.label}
                  </StyledDropdownButton>
                ))}
              </StyledDropdownContent>
            )
          }
        </StyledDropdown>
      </InputGroup>
      {intervalUnit === RecurrenceIntervalUnit.week && (
        <>
          <StyledLabel>Repeats On:</StyledLabel>
          <StyledDropdown
            trigger={
              <Button iconRight>
                {dayOfWeek ? capitalize(dayOfWeek) : "Completion Day"}
                <ChevronDown />
              </Button>
            }
          >
            {({ isOpen, closePortal }) =>
              isOpen && (
                <StyledDropdownContent>
                  {dayOfWeekOptions.map(option => (
                    <StyledDropdownButton
                      key={option.label}
                      onClick={() => {
                        requestAnimationFrame(() => {
                          closePortal();
                          onDayOfWeekClick(option.value ? [option.value] : []);
                        });
                      }}
                    >
                      {option.label}
                    </StyledDropdownButton>
                  ))}
                </StyledDropdownContent>
              )
            }
          </StyledDropdown>
        </>
      )}

      <Link
        onClick={() =>
          requestAnimationFrame(() => {
            setRecurrence(null);
          })
        }
      >
        Clear Repeat
      </Link>
    </FooterSection>
  );
};
